import Pusher, { type Channel } from 'pusher-js'

import { config, type GetToken } from '~/config'

let instance: Pusher | null = null
let latestGetToken: GetToken | null = null

/**
 * Singleton pusher-js client, mirroring Nova's broadcast-auth pattern: private
 * and presence channels authorize by POSTing the socket/channel to the Laravel
 * broadcast endpoint with a Bearer token. The token getter is kept fresh so the
 * client survives Auth0 token rotation without being recreated.
 */
export function getPusher(getToken: GetToken): Pusher {
  latestGetToken = getToken
  if (instance) return instance

  // Debug: ?pusherlog turns on pusher-js console logging (connection, channel
  // subscriptions, and every event received) — used to diagnose realtime.
  if (
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('pusherlog')
  ) {
    Pusher.logToConsole = true
  }

  instance = new Pusher(config.pusherKey, {
    cluster: config.pusherCluster,
    forceTLS: true,
    channelAuthorization: {
      endpoint: config.pusherAuthEndpoint,
      transport: 'ajax',
      customHandler: ({ socketId, channelName }, callback) => {
        void (async () => {
          try {
            const token = latestGetToken ? await latestGetToken() : null
            if (!token) {
              callback(new Error('pusher auth: no token'), null)
              return
            }
            const res = await fetch(config.pusherAuthEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Authorization: `Bearer ${token}`,
              },
              body: `socket_id=${encodeURIComponent(socketId)}&channel_name=${encodeURIComponent(channelName)}`,
            })
            if (!res.ok) {
              callback(new Error(`pusher auth failed: ${res.status}`), null)
              return
            }
            const data = (await res.json()) as {
              auth: string
              channel_data?: string
            }
            callback(null, data)
          } catch (error) {
            callback(
              error instanceof Error ? error : new Error('pusher auth error'),
              null,
            )
          }
        })()
      },
    },
  })

  return instance
}

export function disconnectPusher(): void {
  instance?.disconnect()
  instance = null
  latestGetToken = null
}

export type { Channel }
